import { isFunction, isNil, isString } from 'lodash'

import { GenericalRecord } from '../../types/utilities'
import { omit } from '../object'
import { stringify, stringifyUrl } from '../querystring'
import { normalizeUrl } from '../url'

import {
  RequestConfig,
  BaseConfig,
  RequestMethod,
  RequestOptions,
  CoreOptions
} from './interface'

const createSignal = (timeout?: number) => {
  const controller = new AbortController()
  const timer = setTimeout(() => {
    controller.abort()
  }, timeout)
  return [
    controller.signal,
    () => {
      clearTimeout(timer)
    }
  ] as const
}

export const mergeRequestOptions = <Config extends BaseConfig>(
  { baseURL, headers: defaultHeaders, ...restConfig }: Config,
  { url, headers, ...restOptions }: RequestOptions<Config>
) => {
  const mergedUrl = baseURL ? normalizeUrl(baseURL, url) : url
  const mergedHeaders = {
    ...defaultHeaders,
    ...headers
  }
  return {
    url: mergedUrl,
    headers: mergedHeaders,
    ...restConfig,
    ...restOptions
  } as RequestOptions<Config>
}

const request = async ({
  url,
  method,
  headers,
  params,
  data,
  timeout,
  credentials
}: CoreOptions) => {
  const resolvedUrl = stringifyUrl({
    url,
    query: params
  })
  const [signal, clearSignal] = createSignal(timeout)
  const body = resolveData(method, data, headers)

  return fetch(resolvedUrl, {
    mode: 'cors',
    redirect: 'follow',
    credentials,
    signal,
    method,
    body,
    headers: headers as any
  }).finally(clearSignal)
}

const resolveData = (
  method: RequestMethod,
  data?: RequestOptions<BaseConfig>['data'],
  headers?: RequestOptions<BaseConfig>['headers']
) => {
  if (!(method === 'POST' || method === 'PUT')) {
    return undefined
  }

  const contentType = headers?.['Content-Type']

  if (isNil(data) || isString(data) || data instanceof URLSearchParams) {
    return data
  }

  return contentType?.includes('application/x-www-form-urlencoded')
    ? stringify(data)
    : contentType?.includes('application/json')
      ? JSON.stringify(data)
      : (data as Exclude<typeof data, GenericalRecord>)
}

export const createRequest = <Config extends BaseConfig = BaseConfig>(
  configInit?: RequestConfig<Config> | (() => RequestConfig<Config>)
) => {
  return async <Result>(options: RequestOptions<Config>) => {
    const { transformRequest, onSuccess, onFail, ...globalConfig } = isFunction(
      configInit
    )
      ? configInit()
      : configInit || {}
    const mergedOptions = mergeRequestOptions(globalConfig as Config, options)

    let transformedOptions = mergedOptions
    if (transformRequest) {
      transformedOptions = await transformRequest(mergedOptions)
    }
    const baseConfig = omit(transformedOptions, [
      'url',
      'method',
      'data'
    ]) as any
    try {
      const res = await request(transformedOptions)
      return (onSuccess?.(res, baseConfig) ?? res) as Result
    } catch (err: any) {
      return Promise.reject(onFail?.(err, baseConfig) ?? err)
    }
  }
}
